/**
 * Aerial perspective: far surfaces dissolve into the sky radiance just above the horizon in their own azimuth
 * (sampled from the sun and moon sky-view LUTs), so each ridge fades into its own piece of sky. Applied in linear
 * HDR before tone mapping. Density: 1 − exp(−d / scale · e^(−h / height)), h above sea level.
 */
import * as THREE from 'three';
import { ATMOSPHERE } from '../sky/atmosphere';

export const AERIAL_UNIFORMS = {
  uAerialSky: { value: null as THREE.Texture | null },
  uAerialSkyMoon: { value: null as THREE.Texture | null },
  uAerialSunI: { value: 20 },
  uAerialMoonI: { value: 0 },
  uAerialCamR: { value: ATMOSPHERE.groundRadiusM + 81.6 },
  uAerialAirglow: { value: new THREE.Vector3() },
  uAerialScale: { value: 12_000 },
  uAerialHeight: { value: 600 },
  uAerialSeaY: { value: -80 },
  /** Artistic night dimming of the haze colour (moonlit haze would otherwise wash the sea and hills white). */
  uAerialBright: { value: 1 },
};

export const AERIAL_VERT_PARS = 'varying vec3 vAerialWorld;';
export const AERIAL_VERT = `
#ifdef USE_INSTANCING
  vAerialWorld = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
#else
  vAerialWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
#endif`;

export const AERIAL_FRAG_PARS = `
uniform sampler2D uAerialSky;
uniform sampler2D uAerialSkyMoon;
uniform float uAerialSunI;
uniform float uAerialMoonI;
uniform float uAerialCamR;
uniform vec3 uAerialAirglow;
uniform float uAerialScale;
uniform float uAerialHeight;
uniform float uAerialSeaY;
uniform float uAerialBright;
varying vec3 vAerialWorld;
vec2 aerialSkyUv(vec3 d) {
  const float Rg = ${ATMOSPHERE.groundRadiusM}.0;
  float r = uAerialCamR;
  float beta = acos(sqrt(max(0.0, r * r - Rg * Rg)) / r);
  float zh = 3.14159265 - beta;
  float zen = acos(clamp(d.y, -1.0, 1.0));
  float v = zen <= zh ? (1.0 - sqrt(max(0.0, 1.0 - zen / zh))) * 0.5 : 0.5 + sqrt(min(1.0, (zen - zh) / beta)) * 0.5;
  float u = atan(d.x, -d.z) / 6.28318531;
  return vec2(u < 0.0 ? u + 1.0 : u, v);
}`;

export const AERIAL_FRAG_APPLY = `
{
  vec3 toFrag = vAerialWorld - cameraPosition;
  float dist = length(toFrag);
  vec3 vd = toFrag / max(dist, 1e-3);
  vec3 hd = normalize(vec3(vd.x, 0.035, vd.z));
  vec2 auv = aerialSkyUv(hd);
  vec3 fogC = (texture2D(uAerialSky, auv).rgb * uAerialSunI + texture2D(uAerialSkyMoon, auv).rgb * uAerialMoonI + uAerialAirglow) * uAerialBright;
  float h = max(0.0, vAerialWorld.y - uAerialSeaY);
  float amt = 1.0 - exp(-dist / uAerialScale * exp(-h / uAerialHeight));
  gl_FragColor.rgb = mix(gl_FragColor.rgb, fogC, clamp(amt, 0.0, 1.0));
}`;
